import styled from 'styled-components';

import { ErrorMessage as DropdownError } from '@components/Dropdown/Dropdown.style';

/**
 * Shared layout for the medication form: its section wrappers and field rows.
 *
 * `ColumnLayout` can't be used for these: its `div { flex: 1 }` is a descendant
 * rule, so it outranks any class on a child and every field comes out the same
 * width. A row of "[875] [mg] per [1] [tablet]" then gives a two-character
 * number the same space as a dropdown, which is what curators were reading as
 * clutter.
 */
export const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  flex-wrap: wrap;
`;

/**
 * How much room a field gets, by what it holds. Sized to the widest label each
 * kind carries, since a label that wraps makes its cell taller than the ones
 * beside it and the inputs stop lining up.
 */
type CellSize = 'number' | 'numberWide' | 'text' | 'unit';

const CELL_BASIS: Record<CellSize, string> = {
  // Two to four digits. Anything wider just reads as an empty box.
  number: '0 1 100px',
  // A number whose label is a phrase: "Quantity to", "Max per period".
  numberWide: '0 1 168px',
  unit: '0 1 148px',
  text: '1 1 220px',
};

const CELL_MIN: Record<CellSize, string> = {
  number: '84px',
  numberWide: '132px',
  unit: '116px',
  text: '180px',
};

export const FieldCell = styled.div<{ $size: CellSize }>`
  flex: ${(props) => CELL_BASIS[props.$size]};
  min-width: ${(props) => CELL_MIN[props.$size]};

  /* The field components render a full-width root, so the cell sets the size. */
  > div {
    width: 100%;
  }
`;

/** Keeps a row's remove control aligned with the inputs beside it. */
export const RemoveCell = styled.span`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  /* One label line-height, so the button starts where the inputs do rather than
     where their labels do. */
  padding-top: 1.33rem;
`;

/**
 * Short curator-facing note under a section label. Deliberately quiet: it should
 * read as guidance, not compete with the fields.
 */
export const SectionHelp = styled.p`
  margin: 0 0 ${(props) => props.theme.spacing(1)};
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: ${(props) => props.theme.typography.caption.fontSize}px;
  line-height: 1.4;
`;

/**
 * A hint about a whole row rather than one input. The fields are narrow now, so
 * the same text as a field's `helperText` would stack into a tall thin column
 * under a two-digit box and push the row apart.
 */
export const RowHelp = styled(SectionHelp)`
  margin-top: -${(props) => props.theme.spacing(0.5)};
`;

/**
 * A row that reads as a sentence: "Each tablet contains [3] [mg]". The words
 * around the inputs carry the meaning, so the fields sit inline with them
 * rather than in a labelled grid.
 */
export const SentenceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing(1)};
  width: 100%;
  font-size: ${(props) => props.theme.typography.body1.fontSize}px;
  color: ${(props) => props.theme.palette.text.primary};
`;

/**
 * A field inside a sentence row. Its own label is hidden from sight — the words
 * beside it already say what it holds — but left in the accessibility tree, or
 * a screen reader reaches three unlabelled boxes in a row.
 */
export const SentenceCell = styled(FieldCell)`
  /* A field error under a 100px cell wraps into a tall thin column and pushes
     the words in the row out of line with the inputs. Floating it only moved
     the problem: two errors in one row anchor to their own cells and cover each
     other, and a three-line message lands on whatever follows. The row collects
     them into one line underneath instead - see SentenceErrors. */
  .MuiFormHelperText-root,
  ${DropdownError} {
    display: none;
  }

  [data-testid='input-label'] {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

/**
 * A boxed section of the medication form. "How much" and "How often" are the two
 * questions a curator is actually answering, so each one is a bordered block
 * with its heading and its guidance in a tinted bar rather than a loose label
 * over a run of inputs.
 */
export const SectionBlock = styled.div`
  width: 100%;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
  margin-bottom: ${(props) => props.theme.spacing(1.5)};
  overflow: hidden;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing(1)};
  padding: ${(props) => props.theme.spacing(1.25, 1.5)};
  background-color: ${(props) => props.theme.palette.action.hover};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

export const SectionTitle = styled.span`
  font-size: ${(props) => props.theme.typography.body1.fontSize}px;
  font-weight: 500;
`;

export const SectionHint = styled.span`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: ${(props) => props.theme.typography.caption.fontSize}px;
`;

export const SectionBody = styled.div`
  padding: ${(props) => props.theme.spacing(1.5)};
`;

/**
 * Marks a whole section required rather than any field in it. A `*` on Strength
 * and a `*` on Dose would read as "both", which is the opposite of the rule.
 */
export const RequiredBadge = styled.span`
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.contrastText};
  border-radius: 999px;
  padding: ${(props) => props.theme.spacing(0.25, 1)};
  font-size: ${(props) => props.theme.typography.caption.fontSize}px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`;

/**
 * A failure that belongs to a section rather than to one of its fields. Give it
 * `role="alert"`: it appears after a failed submit, and without one a screen
 * reader is told nothing about why the save did not happen.
 */
export const SectionError = styled.p`
  margin: 0 0 ${(props) => props.theme.spacing(1)};
  color: ${(props) => props.theme.palette.error.main};
  font-size: ${(props) => props.theme.typography.caption.fontSize}px;
`;

/**
 * Spacing for the label of one half of a section. It wraps a real `InputLabel`
 * rather than restyling one: "Strength" heads a row of inputs the same way
 * "Frequency" heads its dropdown, and hand-matching the type drifted from it.
 */
export const SubsectionLabel = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(1)};
`;

/**
 * The word in a sentence row that the Form dropdown fills in. Coloured so it
 * reads as a value the curator chose rather than as fixed copy — otherwise
 * "each tablet contains" looks like a sentence about tablets specifically.
 */
export const DynamicNoun = styled.span`
  color: ${(props) => props.theme.palette.primary.main};
  font-weight: 500;
`;

/**
 * A row-level note about something wrong but not blocking — a stored value this
 * form can't offer. Louder than `RowHelp`, quieter than a validation error,
 * because the save is still allowed.
 */
export const RowWarning = styled(SectionHelp)`
  margin-top: -${(props) => props.theme.spacing(0.5)};
  color: ${(props) => props.theme.palette.warning.main};
`;

/**
 * Every validation message from one sentence row, on one line beneath it. The
 * fields are too narrow to carry their own: a message wraps into a tall column
 * and knocks the row's words out of line with its inputs.
 */
export const SentenceErrors = styled.p`
  margin: ${(props) => props.theme.spacing(0.25, 0, 1)};
  color: ${(props) => props.theme.palette.error.main};
  font-size: ${(props) => props.theme.typography.caption.fontSize}px;
  line-height: 1.4;
`;

/**
 * Opens the upper half of a dose range. Quiet, inline and after the sentence:
 * most doses are a single number, and an always-present "to ___" box reads as a
 * field someone forgot to fill in.
 */
export const RangeButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${(props) => props.theme.palette.primary.main};
  font-family: inherit;
  font-size: ${(props) => props.theme.typography.caption.fontSize}px;
  text-decoration: underline;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.palette.primary.main};
    outline-offset: 2px;
  }
`;
